import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent, AnalyticsLinkAction, AnalyticsView } from '@/enums/analytics-type';
import { AnalyticsBaseType, AnalyticsDataLayer } from '@/types/analytics';
import { convertKeysToCamelCase } from '@/utils/camel-caser';
import { globalWindow } from '@/utils/global-window';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEnqueueAnalyticsItem } from './analytics-queue';

type UseDataAnalyticsActionsType = {
  trackClick: (data: Record<string, AnalyticsBaseType>) => void;
  trackEvent: (eventName: AnalyticsEvent, dataNotInDataLayer?: Record<string, AnalyticsBaseType>) => void;
  trackView: (pageName: string, eventName: AnalyticsView, dataNotInDataLayer?: Record<string, AnalyticsBaseType>) => void;
};

export const useAnalyticsReady = (): ReturnType<typeof useQuery<boolean>> => {
  return useQuery<boolean>({
    queryFn: () => {
      return !!globalWindow?.utag;
    },
    queryKey: QueryKeys.global.analyticsReady,
    staleTime: Infinity,
  });
};

export const useDataAnalytics = (): UseDataAnalyticsActionsType => {
  const { mutate: enqueue } = useEnqueueAnalyticsItem();

  const mapPageType = (eventName: AnalyticsView): string => {
    switch (eventName) {
      case AnalyticsView.ACCOUNT_VIEW:
        return 'account_view';
      case AnalyticsView.BADGES:
        return 'badges';
      case AnalyticsView.CART_VIEW:
        return 'cart';
      case AnalyticsView.CHECKOUT_VIEW:
        return 'checkout';
      case AnalyticsView.FOOD_AND_DRINK:
        return 'food and drink';
      case AnalyticsView.HOME_PAGE:
        return 'home page';
      case AnalyticsView.LOYALTY:
        return 'loyalty';
      case AnalyticsView.MDP_VIEW:
        return 'mdp';
      case AnalyticsView.MOVIE_LIST_VIEW:
        return 'Movies';
      case AnalyticsView.REFUND_START:
        return 'refund';
      case AnalyticsView.SEARCH:
        return 'search';
      case AnalyticsView.SEAT_MAP:
        return 'seat_map';
      case AnalyticsView.SIAT_VIEW:
        return 'food and drink';
      case AnalyticsView.THEATER_VIEW:
        return 'theatres';
      default:
        return 'page_view';
    }
  };

  const mapDepartment = (eventName: AnalyticsEvent | AnalyticsView): string[] => {
    const departmentName: string[] = [];
    switch (eventName) {
      case AnalyticsEvent.CART_ADD:
        departmentName.push('movies');
        break;
      case AnalyticsView.FOOD_AND_DRINK:
        departmentName.push('concessions');
        break;
      case AnalyticsView.LOYALTY:
        departmentName.push('subscriptions');
        break;
      case AnalyticsView.MDP_VIEW:
        departmentName.push('movies');
        break;
      case AnalyticsEvent.PURCHASE:
        departmentName.push('movies');
        break;
      case AnalyticsView.SEAT_MAP:
        departmentName.push('movies');
        break;
      default:
        departmentName.push('');
        break;
    }

    return Array.from(departmentName);
  };

  const mapMovieStatus = (hasShowtimes: boolean, releaseDate?: string | string[]): string => {
    const releaseDateValue = Array.isArray(releaseDate) ? releaseDate[0] : releaseDate;
    const trueReleaseDate = releaseDateValue ? new Date(releaseDateValue) : new Date('0001-01-01T00:00:00.000Z');
    const isReleased = trueReleaseDate.getTime() <= new Date().getTime();

    if (isReleased) {
      if (hasShowtimes) {
        return 'prime ticketing';
      }

      return 'post ticketing';
    } else if (hasShowtimes) {
      return 'advanced ticketing';
    }

    return 'future release';
  };

  const trackClick = (data: Record<string, AnalyticsBaseType>): void => {
    data.linkAction = AnalyticsLinkAction.CLICK;
    enqueue({ data, eventName: AnalyticsEvent.LINK_CLICK });
  };

  const trackEvent = (eventName: AnalyticsEvent, dataNotInDataLayer?: Record<string, AnalyticsBaseType>): void => {
    enqueue({ data: { ...dataNotInDataLayer, productDepartment: mapDepartment(eventName) }, eventName });
  };

  const trackView = (pageName: string, eventName: AnalyticsView, dataNotInDataLayer?: Record<string, AnalyticsBaseType>): void => {
    const pageType = mapPageType(eventName);
    const movieStatus = eventName === AnalyticsView.MDP_VIEW
      ? mapMovieStatus(!!dataNotInDataLayer?.hasShowtimes, dataNotInDataLayer?.movieReleaseDate as string | string[] | undefined) : undefined;

    enqueue({
      data: {
        ...dataNotInDataLayer,
        ...movieStatus !== undefined && { movieStatus },
        pageName: pageType === 'home page' ? 'home page' : pageName,
        pageType,
      },
      eventName,
    });
  };

  return { trackClick, trackEvent, trackView };
};

/**
 * This hook should never be used directly, this should only be used in the AnalyticsProvider
 */
export const useAnalyticsDataLayer = (): ReturnType<typeof useQuery<AnalyticsDataLayer>> => {
  return useQuery<AnalyticsDataLayer>({
    queryFn: () => {
      let response: AnalyticsDataLayer = {};
      const keys = Object.keys(globalWindow?.utag?.data || {});
      if (globalWindow?.utag?.data && keys.length) {
        response = { ...convertKeysToCamelCase(globalWindow.utag.data) } as AnalyticsDataLayer;
      }

      return response;
    },
    queryKey: QueryKeys.user.analytics,
    staleTime: Infinity,
  });
};

type UpdateAnalyticsDataLayerParams = Record<string, AnalyticsBaseType>;

/**
 * This hook should never be used directly, this should only be used in the AnalyticsProvider
 */
export const useUpdateAnalyticsDataLayer = (): ReturnType<typeof useMutation<void, unknown, UpdateAnalyticsDataLayerParams>> => {
  const queryClient = useQueryClient();
  const { mutate: enqueue } = useEnqueueAnalyticsItem();

  return useMutation({
    mutationFn: (data: UpdateAnalyticsDataLayerParams) => {
      enqueue({ data, eventName: AnalyticsEvent.DATA_LAYER });
      return Promise.resolve();
    },
    onError: (_err, _newData, context) => {
      queryClient.setQueryData(QueryKeys.user.analytics, context?.previousData);
    },
    onMutate: async (data: Record<string, AnalyticsBaseType>) => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.analytics });
      const previousData = queryClient.getQueryData<AnalyticsDataLayer>(QueryKeys.user.analytics);
      queryClient.setQueryData<AnalyticsDataLayer>(QueryKeys.user.analytics, old => ({
        ...old,
        ...data,
      }));

      return { previousData };
    },
  });
};
